import moment from 'moment-timezone'

export const USER_TIME_ZONE = moment.tz.guess(true)
export const DEFAULT_TIME_FORMAT = 'MMM DD, YYYY'
export const ONE_SECOND = 1000
export const ONE_MINUTE = ONE_SECOND * 60
export const ONE_HOUR = ONE_MINUTE * 60
export const ONE_DAY = ONE_HOUR * 24

export function toReadableTime(props: { timestamp: number; timezone?: string; format?: string }): string {
  let { timezone, format } = props
  if (!timezone) {
    timezone = USER_TIME_ZONE
  }
  if (!format) {
    format = DEFAULT_TIME_FORMAT
  }

  return moment(props.timestamp)
    .tz(timezone)
    .format(removeAbbr(timezone, format))
}

export function toTimestamp(readableTime: string, timezone?: string): number {
  if (!timezone) {
    timezone = USER_TIME_ZONE
  }
  const d = moment.tz(readableTime, timezone)
  return d.valueOf()
}

export function readableTimeSince(props: { timestamp: number; timezone?: string }): string {
  if (!props.timestamp) {
    return 'no data'
  }
  let now = currentTimestamp()
  if (now - props.timestamp < ONE_MINUTE) {
    return 'just now'
  } else if (now - props.timestamp < ONE_HOUR) {
    let minutes = Math.round((now - props.timestamp) / ONE_MINUTE)
    if (minutes === 1) {
      return '1 min ago'
    } else {
      return minutes + ' mins ago'
    }
  } else if (now - props.timestamp < ONE_DAY) {
    let hours = Math.round((now - props.timestamp) / ONE_HOUR)
    if (hours === 1) {
      return '1 hour ago'
    } else {
      return hours + ' hours ago'
    }
  } else {
    let days = Math.round((now - props.timestamp) / (24 * ONE_HOUR))
    if (days === 1) {
      return '1 day ago' // todo, maybe add a 'yesterday' option? This code is just for demo for now.
    } else {
      return days + ' days ago'
    }
  }
}

export function currentTimestamp(): number {
  return moment().valueOf()
}

export function isFresh(timestamp: number): boolean {
  const nowMoment = moment()
  const dow = nowMoment.day()
  const MONDAY: number = 1

  const now = currentTimestamp()

  let offset
  // 1 for monday
  // If today is monday, the fresh date is extended
  if (dow === MONDAY) {
    offset = 3
  } else {
    offset = 2
  }

  return now - offset * ONE_DAY <= timestamp
}

export async function timeout(ms): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function getStartTimestamp(lookBackDays: number): number {
  const returnVal = moment()
    .add(-lookBackDays, 'days')
    .valueOf()
  return returnVal
}

export function removeAbbr(dataTimezone: string, formatText): string {
  return moment.tz.guess() === dataTimezone ? formatText.replace(/z/i, '').trim() : formatText
}

export function getStartOfDay(options: { timestamp: number; timezone: string; baseHour: number }): number {
  const { timestamp, timezone, baseHour } = options
  const date = moment(timestamp).tz(timezone)
  const isYesterday = date.hours() <= baseHour - 1
  const mDate = moment
    .tz(
      {
        year: date.year(),
        month: date.month(),
        day: date.date(),
        hour: baseHour || 0,
      },
      timezone
    )
    .add(isYesterday ? -1 : 0, 'days')
  return mDate.unix() * 1000
}

export function waitFor(delay: number) {
  return new Promise(resolve => setTimeout(resolve, delay))
}

export function getMorningAfternoonEvening() {
  const currentHour = parseInt(moment().format('HH'))
  if (currentHour >= 0 && currentHour < 12) {
    return 'morning'
  } else if (currentHour <= 18) {
    return 'afternoon'
  } else {
    return 'evening'
  }
}
